import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Download, Check, Shield, Eye, MessageSquare, Users, Heart, Lock, Scale, Lightbulb } from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BonusPage = () => {
  const [pdfInfo, setPdfInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPdfInfo();
  }, []);

  const fetchPdfInfo = async () => {
    try {
      const response = await axios.get(`${API}/bonus/pdf-info`);
      setPdfInfo(response.data);
    } catch (error) {
      console.error("Error fetching PDF info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/bonus/download-pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '15-Ways-To-Stop-A-Narcissist.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      // Fallback: generate and download text content
      const content = generatePDFContent();
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '15-Ways-To-Stop-A-Narcissist.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const generatePDFContent = () => {
    return `
15 WAYS TO STOP A NARCISSIST IN THEIR TRACKS
By ThryvLife
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

INTRODUCTION
Dealing with a narcissist can be one of the most challenging experiences of your life. 
This guide provides practical strategies to protect yourself and reclaim your power.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. RECOGNIZE GASLIGHTING
Learn to identify when someone is making you question your own reality. Trust your 
instincts and keep records of conversations when possible.

2. SET UNSHAKEABLE BOUNDARIES  
Establish clear limits and maintain them consistently. Narcissists will test your 
boundaries repeatedly—stay firm.

3. THE GREY ROCK METHOD
Become as uninteresting as a grey rock. Give short, boring responses. Don't engage 
emotionally. This removes their supply of drama.

4. DOCUMENT EVERYTHING
Keep detailed records of incidents, conversations, and behaviors. This protects you 
legally and helps you maintain clarity.

5. BUILD YOUR SUPPORT NETWORK
Surround yourself with trusted friends, family, or a therapist who understands 
narcissistic abuse. You need people who believe you.

6. TRUST YOUR INSTINCTS
If something feels wrong, it probably is. Narcissists are experts at making you 
doubt yourself. Your gut feelings are valid.

7. LIMIT INFORMATION SHARING
The less they know about your life, plans, and emotions, the less ammunition they 
have to use against you.

8. AVOID JADE (Justify, Argue, Defend, Explain)
Don't try to justify your decisions or argue your point. Narcissists use these 
conversations to manipulate and exhaust you.

9. PRACTICE EMOTIONAL DETACHMENT
Learn to observe their behavior without emotional reaction. This protects your 
mental health and removes their power.

10. KNOW WHEN TO GO NO-CONTACT
Sometimes the healthiest choice is complete separation. If possible, cut all ties 
and maintain zero contact.

11. PROTECT YOUR MENTAL HEALTH
Prioritize self-care, therapy, and healing. Narcissistic abuse causes real trauma 
that requires real recovery.

12. UNDERSTAND THEIR TACTICS
Knowledge is power. Learn about love bombing, devaluation, hoovering, and other 
manipulation techniques.

13. LEGAL CONSIDERATIONS
Consult with attorneys familiar with narcissistic abuse for custody, divorce, or 
harassment situations.

14. RECLAIM YOUR SELF-WORTH
Remember: their treatment of you is not a reflection of your value. You deserve 
love, respect, and kindness.

15. MOVE FORWARD WITH CONFIDENCE
Healing is possible. Many survivors go on to live fulfilling, joyful lives. Your 
story doesn't end with them.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

REMEMBER: You are not alone. Recovery is possible. You deserve peace.

For emergencies: National Domestic Violence Hotline: 1-800-799-7233
Crisis Text Line: Text HOME to 741741

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
© 2025 ThryvLife - All Rights Reserved
www.brivonb.com
`;
  };

  const topicIcons = [
    Eye, MessageSquare, Shield, FileText, Users, 
    Lock, Heart, Scale, Lightbulb, Check
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      
      <section className="pt-28 pb-24 px-6 lg:px-12" data-testid="bonus-section">
        <div className="container mx-auto max-w-5xl">
          {/* Back Link */}
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-8">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>

          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-[#A69BE0] mb-4">
              Exclusive Bonus
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#1A2A3A] mb-4">
              {pdfInfo?.title || "15 Ways to Stop a Narcissist"}
            </h1>
            <p className="text-lg text-[#1A2A3A]/60 max-w-2xl mx-auto">
              {pdfInfo?.description || "Your comprehensive guide to recognizing manipulation tactics and responding with strength."}
            </p>
          </div>

          {/* PDF Preview Card */}
          <Card className="pdf-section rounded-3xl overflow-hidden mb-12" data-testid="pdf-preview-card">
            <CardContent className="p-8 md:p-12 relative z-10">
              <div className="flex flex-col md:flex-row items-center gap-8">
                {/* PDF Icon/Preview */}
                <div className="w-48 h-64 bg-white rounded-2xl shadow-floating flex flex-col items-center justify-center p-6">
                  <FileText className="h-16 w-16 text-[#A69BE0] mb-4" />
                  <p className="font-heading text-lg font-semibold text-[#1A2A3A] text-center">
                    PDF Guide
                  </p>
                  <p className="text-sm text-[#1A2A3A]/60 text-center mt-2">
                    15 Powerful Strategies
                  </p>
                </div>

                {/* Content */}
                <div className="flex-1 text-white">
                  <h2 className="font-heading text-3xl font-bold mb-4 text-[#1A2A3A]">
                    Your Protection Playbook
                  </h2>
                  <p className="text-[#1A2A3A]/80 mb-6 leading-relaxed">
                    This comprehensive guide gives you actionable strategies to protect yourself, 
                    set boundaries, and respond to narcissistic behavior with confidence. Learn to 
                    recognize manipulation tactics before they affect you.
                  </p>
                  
                  <Button 
                    onClick={handleDownload}
                    className="bg-white text-[#A69BE0] hover:bg-white/90 rounded-full px-8 py-6 font-semibold text-lg shadow-lg"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="mr-2 h-5 w-5" />
                    Download Your Guide
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Topics Covered */}
          <div className="mb-12">
            <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-6 text-center">
              What You'll Learn
            </h2>
            
            {loading ? (
              <div className="grid md:grid-cols-2 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="animate-pulse flex items-center gap-3 p-4 bg-white rounded-xl">
                    <div className="w-10 h-10 bg-[#E5E7EB] rounded-lg"></div>
                    <div className="h-4 bg-[#E5E7EB] rounded flex-1"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {pdfInfo?.topics?.map((topic, index) => {
                  const IconComponent = topicIcons[index % topicIcons.length];
                  return (
                    <Card 
                      key={index} 
                      className="bg-white rounded-xl shadow-card border border-[#E5E7EB]/50 p-4 hover:shadow-floating transition-all card-hover"
                      data-testid={`topic-${index}`}
                    >
                      <CardContent className="p-0 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-[#DCD6F7]/30 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="h-5 w-5 text-[#A69BE0]" />
                        </div>
                        <span className="text-[#1A2A3A]/80 font-medium">{topic}</span>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Why This Guide Section */}
          <Card className="bg-gradient-to-br from-[#4FB3D4]/5 to-[#DCD6F7]/10 rounded-2xl border-0 p-8 mb-12">
            <div className="max-w-3xl mx-auto">
              <h3 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-6 text-center">
                Why This Guide Matters
              </h3>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#4FB3D4]/10 flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-7 w-7 text-[#4FB3D4]" />
                  </div>
                  <h4 className="font-semibold text-[#1A2A3A] mb-2">Protection</h4>
                  <p className="text-sm text-[#1A2A3A]/60">
                    Learn to shield yourself from emotional manipulation
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#A69BE0]/20 flex items-center justify-center mx-auto mb-4">
                    <Eye className="h-7 w-7 text-[#A69BE0]" />
                  </div>
                  <h4 className="font-semibold text-[#1A2A3A] mb-2">Awareness</h4>
                  <p className="text-sm text-[#1A2A3A]/60">
                    Recognize tactics before they can harm you
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#FFB7B2]/20 flex items-center justify-center mx-auto mb-4">
                    <Heart className="h-7 w-7 text-[#FFB7B2]" />
                  </div>
                  <h4 className="font-semibold text-[#1A2A3A] mb-2">Healing</h4>
                  <p className="text-sm text-[#1A2A3A]/60">
                    Strategies that support your recovery journey
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* CTA */}
          <div className="text-center">
            <p className="text-[#1A2A3A]/60 mb-6">
              Ready to explore more recovery resources?
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/calendar">
                <Button 
                  variant="outline" 
                  className="rounded-full px-6 py-4 font-medium border-2 border-[#4FB3D4]/20 hover:bg-[#4FB3D4]/5"
                >
                  View Affirmations
                </Button>
              </Link>
              <Link to="/resources">
                <Button 
                  variant="outline" 
                  className="rounded-full px-6 py-4 font-medium border-2 border-[#FFB7B2]/20 hover:bg-[#FFB7B2]/5"
                >
                  Emergency Resources
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button 
                  className="btn-primary rounded-full px-6 py-4 font-semibold"
                >
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-[#1A2A3A] text-white">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="font-heading text-2xl font-bold">ThryvLife</div>
            <nav className="flex gap-8">
              <Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link>
              <Link to="/calendar" className="text-white/70 hover:text-white transition-colors">Calendar</Link>
              <Link to="/resources" className="text-white/70 hover:text-white transition-colors">Resources</Link>
            </nav>
            <p className="text-white/50 text-sm">© 2025 ThryvLife. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BonusPage;
